import { addSlideFooter } from '../slide_footer';
import { FONT_FACE, LINK_COLOR } from '../constants';

/**
 * Composes the metadata body text from already-derived primitives.
 * @param {Object} options
 * @returns {string}
 */
const getMetadataText = ({
  appliedFilters,
  compareBy,
  questionsCount,
  quotaGroupsCount,
  totalResponses,
}) => {
  const downloadDate = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date());

  const metadataLines = [
    `Total Responses: ${totalResponses}`,
    `Downloaded: ${downloadDate}`,
    `Questions: ${questionsCount}`,
    `Quota groups: ${quotaGroupsCount}`,
  ];

  const displayCompareBy =
    compareBy && compareBy !== 'overall' && compareBy.trim() !== '' ? compareBy : 'Overall';
  metadataLines.push(`Compared by: ${displayCompareBy}`);

  const displayFilters =
    appliedFilters && appliedFilters.length > 0 ? appliedFilters.join(', ') : 'None';
  metadataLines.push(`Filtered by: ${displayFilters}`);

  return metadataLines.join('\n');
};

/**
 * Creates a metadata slide: a "Source" hyperlink plus a block of pre-derived summary lines. This is a
 * generic renderer — the caller supplies primitives (counts, compareBy label, applied-filter strings).
 * Feature-specific derivation (e.g. Survey's quota-group/question-option filters) stays in the feature.
 *
 * @param {Object} options
 * @param {String[]} [options.appliedFilters] filter descriptions ("Filtered by: …")
 * @param {String}  [options.compareBy]        compare-by label ("" → "Overall")
 * @param {Object}   options.pptx              PptxGenJS instance
 * @param {Number}  [options.questionsCount]
 * @param {Number}  [options.quotaGroupsCount]
 * @param {String}   options.reportType        footer report type
 * @param {String}   options.reportUrl         footer link
 * @param {String}   options.sourceLabel       hyperlink text (e.g. survey title)
 * @param {String}   options.sourceUrl         hyperlink target
 * @param {Number}  [options.totalResponses]
 * @returns {Object} Created slide
 */
export const createMetadataSlide = ({
  appliedFilters = [],
  compareBy = '',
  pptx,
  questionsCount = 0,
  quotaGroupsCount = 1,
  reportType,
  reportUrl,
  sourceLabel,
  sourceUrl,
  totalResponses = 0,
}) => {
  const slide = pptx.addSlide();

  const metadataText = getMetadataText({
    appliedFilters,
    compareBy,
    questionsCount,
    quotaGroupsCount,
    totalResponses,
  });

  slide.addText(
    [
      { text: 'Source: ' },
      {
        options: { hyperlink: { url: sourceUrl } },
        text: sourceLabel,
      },
    ],
    {
      align: 'left',
      color: LINK_COLOR,
      fontFace: FONT_FACE,
      fontSize: 10,
      italic: true,
      lineSpacing: 14,
      valign: 'top',
      w: '90%',
      x: 0.5,
      y: 1.0,
    }
  );

  slide.addText(metadataText, {
    align: 'left',
    color: LINK_COLOR,
    fontFace: FONT_FACE,
    fontSize: 10,
    italic: true,
    lineSpacing: 14,
    valign: 'top',
    w: '90%',
    x: 0.5,
    y: 1.2,
  });

  addSlideFooter({ pptx, reportType, reportUrl, slide });

  return slide;
};
